/**
 * `/sign-in` — the page an operator lands on when a management surface turns
 * them away (contracts/protected-surfaces.md §4).
 *
 * There is no Rails route behind this path. The catch-all serves the same SPA
 * shell it serves every other non-XHR HTML path, and the router picks the page
 * from there, which is why a redirect out of `/sidekiq` can point at a URL no
 * `routes.rb` line mentions.
 *
 * ## Layout only
 *
 * Everything with a decision in it — the two fields, the pending discipline,
 * what a 401 says, where a success navigates — lives in `sign-in-form.tsx`,
 * including this page's one `<h1>` and the 420 px measure the form holds itself
 * to. This file supplies a column and nothing else, so there is no second
 * heading here and no width fighting the form's own.
 *
 * The arrangement is `not-found.tsx`'s, for the same reason: a short page whose
 * body is one centred block. A 1100 px column with no inset of its own wraps a
 * section carrying `spacing.md` on the horizontal axis, and that single gutter
 * is what keeps a 320 px viewport reflowing without horizontal scroll
 * (FR-019(f)). The vertical padding is asymmetric like the not-found page's —
 * the space above is the page's opening breath, the space below only has to
 * keep the submit button off the bottom of the window.
 *
 * There is no footer, so `role="main"` is the page's only landmark besides the
 * header's `banner`.
 *
 * ## No search params
 *
 * `/sign-in?redirect=…` deliberately does not exist. Where to land after a
 * successful sign-in is the server's memory of what was asked for before the
 * redirect (FR-005), returned in the sign-in response body and validated by
 * `resolveRedirectTarget`. A destination that travelled in the URL instead
 * would be an open redirect with a query string for an attack surface, and
 * declaring no `validateSearch` here is what keeps one from being added by
 * habit.
 */

import { createFileRoute } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";
import { SignInForm } from "@/components/operators/sign-in-form";
import { PageColumn } from "@/components/page-column";
import { spacing } from "@/theme/tokens";

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
  head: () => ({
    meta: [
      { title: "Sign in — Sparkie" },
      // A client-rendered hint binds only JS-executing crawlers (Googlebot
      // qualifies); the airtight server-side X-Robots-Tag is out of this
      // SPA's reach and not worth a Rails special case for one page.
      { name: "robots", content: "noindex" },
    ],
  }),
});

function SignInPage() {
  return (
    <PageColumn>
      <View role="main" style={styles.section}>
        <SignInForm />
      </View>
    </PageColumn>
  );
}

const styles = StyleSheet.create({
  section: {
    // The form is narrower than the column at every width above 420 px, so
    // centring it is this section's whole job — the form itself only knows how
    // wide it is willing to be, not where.
    alignItems: "center",
    paddingTop: spacing.xxxl,
    paddingBottom: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
});
